import { createContext, useContext, useState, ReactNode, useCallback } from "react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface AdminAuthContextType {
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  adminFetch: (path: string, options?: RequestInit) => Promise<Response>;
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

const TOKEN_KEY = "admin_jwt";

function base64urlDecode(str: string): string {
  const padded = str.replace(/-/g, "+").replace(/_/g, "/").padEnd(str.length + (4 - (str.length % 4)) % 4, "=");
  return atob(padded);
}

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(base64urlDecode(token.split(".")[1]));
    return typeof payload.exp === "number" && payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      const stored = localStorage.getItem(TOKEN_KEY);
      if (stored && isTokenExpired(stored)) {
        localStorage.removeItem(TOKEN_KEY);
        return null;
      }
      return stored;
    } catch { return null; }
  });

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  const login = async (email: string, password: string) => {
    const res = await fetch(`${BASE}/api/admin/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? "Login failed");
    }
    const { token: t } = await res.json();
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };

  const adminFetch = useCallback(async (path: string, options: RequestInit = {}): Promise<Response> => {
    if (!token) throw new Error("Not authenticated");
    const res = await fetch(`${BASE}/${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers ?? {}),
      },
    });
    if (res.status === 401) {
      logout();
      throw new Error("Session expired — please log in again");
    }
    return res;
  }, [token, logout]);

  const isAuthenticated = !!token && !isTokenExpired(token);

  return (
    <AdminAuthContext.Provider value={{ token, login, logout, isAuthenticated, adminFetch }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  return ctx;
}

